import { apiClient, apiEndpoint } from './api.services';
import { API } from '../constants/api.constants';
import type { IMyTodo, MyTodoFilterValues } from '../Pages/Dashboard/TodoList/MyTodo/store/my-todo.types';

export interface UpdateMyTodoStatusParams {
  id?: string;
  ids?: string[];
  status: 'Pending' | 'Done';
  updates?: Array<{ id: string; status: 'Pending' | 'Done' }>;
}

const FILTER_KEYS = [
  'statuses',
  'brandId',
  'subBrandId',
  'productType',
  'catOneId',
  'catTwoId',
  'catThreeId',
  'catFourId',
  'supplierId',
  'seasonId',
  'year',
  'month',
  'createdBy',
  'productStatus',
  'licensorId',
  'licenseId',
  'likenessId',
  'colorwayId',
  'colorGroupId',
  'textCustomAttribute',
] as const;

const hasProductFilters = (params?: Partial<MyTodoFilterValues>): boolean => {
  if (params == null) return false;
  if (FILTER_KEYS.some((key) => Array.isArray(params[key]) && (params[key] as unknown[]).length > 0)) return true;
  // dynamicTypes is Array<{colId,valId}> — check separately since it's not a string[] key
  if (Array.isArray(params.dynamicTypes) && params.dynamicTypes.length > 0) return true;
  return false;
};

const serializeParams = (params: Partial<MyTodoFilterValues>): Record<string, unknown> => {
  const serialized = { ...params } as Record<string, unknown>;
  FILTER_KEYS.forEach((key) => {
    const val = serialized[key];
    if (Array.isArray(val) && val.length > 0) {
      serialized[key] = val.join(',');
    }
  });
  return serialized;
};

export const getMyTodoList = async (params?: Partial<MyTodoFilterValues>) => {
  try {
    if (hasProductFilters(params)) {
      return await apiClient.post(
        `${apiEndpoint}${API.MY_TODO.POST_MY_TODO_LIST}`,
        params
      );
    }
    const serialized = serializeParams(params ?? {});
    return await apiClient.get(`${apiEndpoint}${API.MY_TODO.GET_MY_TODO_LIST}`, {
      params: serialized,
    });
  } catch (error) {
    console.error('Error in getMyTodoList:', error);
    throw error;
  }
};

export const createMyTodo = async (todoData: Partial<IMyTodo>) => {
  try {
    return await apiClient.post(`${apiEndpoint}${API.MY_TODO.CREATE_MY_TODO}`, todoData);
  } catch (error) {
    console.error('Error in createMyTodo:', error);
    throw error;
  }
};

export const updateMyTodoStatus = async (updateData: UpdateMyTodoStatusParams) => {
  try {
    return await apiClient.patch(`${apiEndpoint}${API.MY_TODO.UPDATE_MY_TODO_STATUS}`, updateData);
  } catch (error) {
    console.error('Error in updateMyTodoStatus:', error);
    throw error;
  }
};

export const getMyTodoCount = async () => {
  try {
    return await apiClient.get(`${apiEndpoint}${API.MY_TODO.GET_MY_TODO_COUNT}`);
  } catch (error) {
    console.error('Error in getMyTodoCount:', error);
    throw error;
  }
};

export const deleteMyTodo = async (data: { id?: string; ids?: string[] }) => {
  try {
    return await apiClient.delete(`${apiEndpoint}${API.MY_TODO.DELETE_MY_TODO}`, { data });
  } catch (error) {
    console.error('Error in deleteMyTodo:', error);
    throw error;
  }
};

export default {
  getMyTodoList,
  createMyTodo,
  updateMyTodoStatus,
  getMyTodoCount,
  deleteMyTodo,
};
